import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';
import { useRBAC } from '../hooks/useRBAC';

const JOB_TYPES = [
  { value: 'permit_scraper', label: 'Permit Scanner', icon: 'fa-file-contract', color: 'primary', desc: 'Scan county permit records for recent home improvement permits (solar, HVAC, roofing)' },
  { value: 'google_solar', label: 'Solar Potential Scanner', icon: 'fa-sun', color: 'secondary', desc: 'Analyze satellite imagery and solar irradiance data for high-potential properties' },
  { value: 'smart_search', label: 'Smart Enrichment', icon: 'fa-magnifying-glass-chart', color: 'accent', desc: 'Data intelligence: property records, ownership, energy usage patterns' },
  { value: 'csv_import', label: 'CSV Batch Import', icon: 'fa-file-csv', color: 'nardo-dark', desc: 'Import and auto-enrich leads from CSV files with contact data' },
];

const STATUS_CONF = {
  running: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20', dot: 'bg-primary' },
  completed: { bg: 'bg-accent/10', text: 'text-accent', border: 'border-accent/20', dot: 'bg-accent' },
  queued: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-500' },
  stopped: { bg: 'bg-destructive-light', text: 'text-destructive', border: 'border-destructive/20', dot: 'bg-destructive' },
};

export default function LeadMining() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newJob, setNewJob] = useState({ name: '', type: 'permit_scraper', region: '', target_count: 50 });
  const [scanProgress, setScanProgress] = useState({});
  const { can } = useRBAC();
  const intervals = useRef({});

  const fetchJobs = () => { api.get('/api/mining/jobs').then(res => { setJobs(res.data.jobs); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { fetchJobs(); return () => Object.values(intervals.current).forEach(clearInterval); }, []);

  const simulateScan = (jobId) => {
    setScanProgress(p => ({ ...p, [jobId]: { pct: 0, phase: 'Connecting to permit database...', found: 0 } }));
    const phases = ['Querying public permit records...', 'Scanning building department filings...', 'Matching property & owner data...', 'Calculating solar scores...', 'Enriching lead profiles...', 'Validating and ranking results...'];
    let step = 0;
    intervals.current[jobId] = setInterval(() => {
      step++;
      const pct = Math.min(step * 8, 100);
      const phase = phases[Math.min(Math.floor(step / 2), phases.length - 1)];
      const found = Math.floor(step * 1.5);
      setScanProgress(p => ({ ...p, [jobId]: { pct, phase, found } }));
      if (pct >= 100) {
        clearInterval(intervals.current[jobId]);
        api.post(`/api/mining/jobs/${jobId}/simulate`).then((res) => {
          fetchJobs();
          toast.success(`Scan complete! ${res.data?.leads_found || 0} enriched leads discovered.`);
        }).catch(() => { toast.error('Scan failed'); });
        setTimeout(() => { setScanProgress(p => { const n = { ...p }; delete n[jobId]; return n; }); }, 500);
      }
    }, 400);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/mining/jobs', newJob);
      toast.success('Scanner created!');
      setShowCreate(false); setNewJob({ name: '', type: 'permit_scraper', region: '', target_count: 50 });
      fetchJobs();
    } catch { toast.error('Failed'); }
  };

  const handleStart = async (jobId) => {
    try { await api.post(`/api/mining/jobs/${jobId}/start`); toast.success('Scanner started!'); fetchJobs(); simulateScan(jobId); } catch { toast.error('Failed'); }
  };

  const handleStop = async (jobId) => {
    clearInterval(intervals.current[jobId]);
    setScanProgress(p => { const n = { ...p }; delete n[jobId]; return n; });
    try { await api.post(`/api/mining/jobs/${jobId}/stop`); toast.success('Scanner stopped'); fetchJobs(); } catch { toast.error('Failed'); }
  };

  const runningCount = jobs.filter(j => j.status === 'running').length;
  const totalFound = jobs.reduce((a, j) => a + (j.leads_found || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-mining-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Lead Mining</h1>
          <p className="text-text-muted text-sm mt-1">Automated scanners to discover and enrich solar leads</p>
        </div>
        {can('create_mining_job') && (
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs py-2.5" data-testid="create-mining-job-btn"><i className="fa-solid fa-plus mr-1.5" />New Scanner</button>
        )}
      </div>

      {/* Scanner Types Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {JOB_TYPES.map((type) => {
          const count = jobs.filter(j => j.type === type.value).length;
          const running = jobs.filter(j => j.type === type.value && j.status === 'running').length;
          const found = jobs.filter(j => j.type === type.value).reduce((a, j) => a + (j.leads_found || 0), 0);
          return (
            <div key={type.value} className="card-interactive p-5" data-testid={`mining-type-${type.value}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl bg-${type.color}/10 flex items-center justify-center`}>
                  <i className={`fa-solid ${type.icon} text-${type.color} text-sm`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">{type.label}</p>
                  {running > 0 && <p className="text-[10px] text-primary font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse-soft" />{running} active</p>}
                </div>
              </div>
              <p className="text-xs text-text-muted mb-3 leading-relaxed">{type.desc}</p>
              <div className="flex items-center gap-4 text-xs text-text-muted">
                <span><strong className="text-text-primary">{count}</strong> jobs</span>
                <span><strong className="text-text-primary">{found}</strong> leads</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Bar */}
      <div className="card-interactive p-4 flex items-center justify-between">
        <div className="flex items-center gap-6 text-sm">
          <span className="text-text-muted">Total Jobs: <strong className="text-text-primary">{jobs.length}</strong></span>
          <span className="text-text-muted">Running: <strong className="text-primary">{runningCount}</strong></span>
          <span className="text-text-muted">Leads Found: <strong className="text-text-primary">{totalFound}</strong></span>
        </div>
      </div>

      {/* Jobs List */}
      <div className="space-y-3">
        {loading ? (
          <div className="card-interactive py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-lg text-text-muted" /></div>
        ) : jobs.length === 0 ? (
          <div className="card-interactive py-20 text-center text-text-muted">
            <i className="fa-solid fa-satellite-dish text-4xl mb-3 block opacity-20" />
            <p className="font-medium">No scanners yet</p>
            <p className="text-xs mt-1">Create your first scanner to start mining leads</p>
          </div>
        ) : jobs.map((job, i) => {
          const conf = STATUS_CONF[job.status] || STATUS_CONF.queued;
          const progress = scanProgress[job.id];
          const typeInfo = JOB_TYPES.find(t => t.value === job.type);
          return (
            <div key={job.id} className="card-interactive p-5 animate-slide-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`mining-job-${job.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl bg-${typeInfo?.color || 'nardo'}/10 flex items-center justify-center`}>
                    <i className={`fa-solid ${typeInfo?.icon || 'fa-circle'} text-${typeInfo?.color || 'nardo-dark'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{job.name}</p>
                    <p className="text-xs text-text-muted">{job.region} &middot; Target: {job.target_count} &middot; Type: {typeInfo?.label}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <p className="text-lg font-bold font-mono text-text-primary tabular-nums">{job.leads_found}</p>
                    <p className="text-[10px] text-text-muted uppercase tracking-wider">Leads Found</p>
                  </div>
                  <span className={`badge ${conf.bg} ${conf.text} border ${conf.border} capitalize`}>
                    {job.status === 'running' && <span className={`w-1.5 h-1.5 ${conf.dot} rounded-full mr-1.5 animate-pulse-soft`} />}
                    {job.status}
                  </span>
                  {job.status === 'queued' && <button onClick={() => handleStart(job.id)} className="btn-primary text-xs py-1.5 px-3" data-testid={`start-job-${job.id}`}><i className="fa-solid fa-play mr-1" />Start</button>}
                  {job.status === 'running' && <button onClick={() => handleStop(job.id)} className="px-3 py-1.5 rounded-xl border border-destructive/20 text-destructive text-xs font-semibold hover:bg-destructive-light transition-all" data-testid={`stop-job-${job.id}`}><i className="fa-solid fa-stop mr-1" />Stop</button>}
                </div>
              </div>
              {/* Scanner Progress Bar */}
              {progress && (
                <div className="mt-4 p-3 rounded-xl bg-surface-alt border border-border animate-fade-in">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-text-secondary font-medium flex items-center gap-1.5"><i className="fa-solid fa-tower-broadcast fa-spin-pulse text-primary" />{progress.phase}</span>
                    <span className="text-xs font-bold text-primary tabular-nums">{progress.pct}%</span>
                  </div>
                  <div className="h-2 bg-border rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-emerald-400 rounded-full transition-all duration-300 relative" style={{ width: `${progress.pct}%` }}>
                      <div className="absolute inset-0 bg-surface/30 animate-scan" />
                    </div>
                  </div>
                  <p className="text-[11px] text-text-muted mt-1.5">{progress.found} leads discovered so far</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)} data-testid="create-job-modal">
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-lg animate-slide-up" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-1">Create New Scanner</h2>
            <p className="text-sm text-text-muted mb-5">Configure an automated lead mining scanner</p>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Scanner Name</label><input type="text" required value={newJob.name} onChange={e => setNewJob({ ...newJob, name: e.target.value })} className="input-field" placeholder="e.g., Sacramento Q1 Permits" data-testid="new-job-name" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Scanner Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {JOB_TYPES.map(t => (
                    <button type="button" key={t.value} onClick={() => setNewJob({ ...newJob, type: t.value })} className={`p-3 rounded-xl border text-left transition-all ${newJob.type === t.value ? 'border-primary bg-primary/5' : 'border-border hover:border-nardo'}`} data-testid={`select-type-${t.value}`}>
                      <div className="flex items-center gap-2 mb-1"><i className={`fa-solid ${t.icon} text-xs text-${t.color}`} /><span className="text-xs font-semibold text-text-primary">{t.label}</span></div>
                      <p className="text-[10px] text-text-muted leading-relaxed">{t.desc.slice(0, 60)}...</p>
                    </button>
                  ))}
                </div>
              </div>
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Target Region</label><input type="text" required value={newJob.region} onChange={e => setNewJob({ ...newJob, region: e.target.value })} className="input-field" placeholder="e.g., Sacramento, CA" data-testid="new-job-region" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Target Lead Count</label><input type="number" min={1} max={500} value={newJob.target_count} onChange={e => setNewJob({ ...newJob, target_count: parseInt(e.target.value) || 50 })} className="input-field" data-testid="new-job-target" /></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="btn-primary text-sm" data-testid="submit-job-btn">Create Scanner</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
